import React, { useEffect, useState, useContext } from "react";
import { useEmployeeAllRegFormModal } from "@hooks/comp/useEmployeeAllRegFormModal";
import { get } from "@utils/axios_api";
import { FormProvider } from "react-hook-form";
import { AuthContext } from "@context/AuthContext";
import { ROLES } from "@constants/roles";

const EmployeeExcelUpload = () => {
    const { auth } = useContext(AuthContext);
    const [selectedCompCode, setSelectedCompCode] = useState("");
    const [selectedCompName, setSelectedCompName] = useState("");
    const [selectedSiteCode, setSelectedSiteCode] = useState("");
    const [selectedSiteName, setSelectedSiteName] = useState("");
    const [companyList, setCompanyList] = useState([]);
    const [siteList, setSiteList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [siteLoading, setSiteLoading] = useState(false);

    const {
        methods,
        handleFormSubmit,
        previewData,
        loading: formLoading,
        handleSampleDownload,
        saveData,
        errorMsg,
    } = useEmployeeAllRegFormModal({
        compCode: selectedCompCode,
        siteCode: selectedSiteCode,
    });

    const { register } = methods;

    // 회사 목록 조회
    useEffect(() => {
        if (auth.role === ROLES.COMP_ADMIN) {
            setCompanyList([{ compCode: auth.compCode, compName: auth.compName }]);
            setSelectedCompCode(auth.compCode);
            setSelectedCompName(auth.compName);
            return;
        }

        const fetchCompanyList = async () => {
            try {
                setLoading(true);
                const response = await get("/admin/corp/corplist", {
                    params: {
                        pageIndex: 1,
                        recordCountPerPage: 1000,
                        pageSize: 10
                    }
                });

                if (response && response.resultList) {
                    setCompanyList(response.resultList);
                } else {
                    setCompanyList([]);
                }
            } catch (error) {
                setCompanyList([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCompanyList();
    }, [auth.role, auth.compCode, auth.compName]);

    // 사이트 목록 조회 (회사 선택 시)
    useEffect(() => {
        const fetchSiteList = async () => {
            if (!selectedCompCode) {
                setSiteList([]);
                return;
            }

            try {
                setSiteLoading(true);
                let siteData = [];

                if (auth.role === ROLES.COMP_ADMIN) {
                    const response = await get("/user/site/siteList", {
                        params: { compCode: selectedCompCode, pageIndex: 1, recordCountPerPage: 1000 }
                    });
                    if (response && response.resultList) {
                        siteData = response.resultList;
                    }
                } else {
                    const response = await get(`/admin/employee/list/${selectedCompCode}`);
                    if (response.status === true && response.result) {
                        siteData = response.result;
                    }
                }

                setSiteList(siteData);
            } catch (error) {
                setSiteList([]);
            } finally {
                setSiteLoading(false);
            }
        };
        fetchSiteList();
    }, [selectedCompCode, auth.role]);

    // 회사 선택 핸들러
    const handleCompanySelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const [compCode, compName] = selectedValue.split("|");
            setSelectedCompCode(compCode);
            setSelectedCompName(compName);
            // 회사 변경 시 사이트 초기화
            setSelectedSiteCode("");
            setSelectedSiteName("");
        } else {
            setSelectedCompCode("");
            setSelectedCompName("");
            setSelectedSiteCode("");
            setSelectedSiteName("");
        }
    };

    // 사이트 선택 핸들러
    const handleSiteSelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const [siteCode, siteName] = selectedValue.split("|");
            setSelectedSiteCode(siteCode);
            setSelectedSiteName(siteName);
        } else {
            setSelectedSiteCode("");
            setSelectedSiteName("");
        }
    };

    return (
        <FormProvider {...methods}>
            <div className="contents-wrap">
                <div className="page-title-wrap">
                    <h2>
                        <span className="icon-dot-circle"></span>
                        사원 일괄 등록
                    </h2>
                </div>

                <div className="tab-cont-wrap">
                    <div className="tab-cont-01 on">
                        <form onSubmit={handleFormSubmit}>
                            <div className="search-wrap">
                                <table className="table-list" style={{width: "100%", marginBottom: "20px"}}>
                                    <tbody>
                                    <tr>
                                        <td colSpan="2" style={{textAlign: "left", padding: "10px"}}>
                                            1. 사원등록 샘플양식을 다운로드 받아 작성한 후 업로드 하십시오.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" style={{textAlign: "left", padding: "10px"}}>
                                            2. <strong>회사 및 사업장 정보는 엑셀에 입력하지 마세요.</strong> 선택한 회사/사업장으로 자동 설정됩니다.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" style={{textAlign: "left", padding: "10px"}}>
                                            3. 부서와 직위는 해당 회사에 실제로 등록된 것만 입력 가능합니다.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" style={{textAlign: "left", padding: "10px"}}>
                                            4. 안전직무는 기준관리 → 공통코드 → SAFE_ROLE의 공통코드명을 정확히 입력하세요.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" style={{textAlign: "left", padding: "10px"}}>
                                            5. 업로드 된 사원 정보를 확인한 후 등록하기 버튼을 클릭하면 사원 등록이 완료됩니다.
                                        </td>
                                    </tr>

                                    {/* 회사 선택 영역 */}
                                    <tr>
                                        <th style={{ width: "20%", padding: "10px" }}>회사 선택</th>
                                        <td style={{ width: "80%", padding: "10px" }}>
                                            <select
                                                value={selectedCompCode ? `${selectedCompCode}|${selectedCompName}` : ""}
                                                onChange={handleCompanySelect}
                                                className="input-customtext"
                                                style={{ width: "300px", marginRight: "10px" }}
                                                disabled={loading}
                                            >
                                                <option value="">회사를 선택하세요</option>
                                                {companyList.map((company, index) => (
                                                    <option
                                                        key={index}
                                                        value={`${company.compCode}|${company.compName}`}
                                                    >
                                                        {company.compName}
                                                    </option>
                                                ))}
                                            </select>
                                            {loading && <span style={{ color: "#666", fontSize: "14px" }}>로딩 중...</span>}
                                        </td>
                                    </tr>

                                    {/* 사이트 선택 영역 */}
                                    {selectedCompCode && (
                                        <tr>
                                            <th style={{ width: "20%", padding: "10px" }}>사업장 선택</th>
                                            <td style={{ width: "80%", padding: "10px" }}>
                                                <select
                                                    value={selectedSiteCode ? `${selectedSiteCode}|${selectedSiteName}` : ""}
                                                    onChange={handleSiteSelect}
                                                    className="input-customtext"
                                                    style={{ width: "300px", marginRight: "10px" }}
                                                    disabled={siteLoading}
                                                >
                                                    <option value="">사업장를 선택하세요</option>
                                                    {siteList.map((site, index) => (
                                                                                                            <option
                                                        key={index}
                                                        value={`${site.siteCode}|${site.siteName}`}
                                                    >
                                                        {site.siteName}
                                                    </option>
                                                    ))}
                                                </select>
                                                {siteLoading && <span style={{ color: "#666", fontSize: "14px" }}>로딩 중...</span>}
                                            </td>
                                        </tr>
                                    )}

                                    {/* 선택된 회사/사이트 정보 표시 */}
                                    {selectedCompCode && selectedSiteCode && (
                                        <tr>
                                            <th style={{width: "20%", padding: "10px"}}>선택된 정보</th>
                                            <td style={{width: "80%", padding: "10px"}}>
                                                <div style={{display: "flex", alignItems: "center", gap: "20px"}}>
                                                    <span style={{fontSize: "16px", fontWeight: "bold", color: "#333"}}>
                                                        {selectedCompName} → {selectedSiteName}
                                                    </span>
                                                    <span style={{fontSize: "14px", color: "#666"}}>
                                                        (회사코드: {selectedCompCode}, 사업장코드: {selectedSiteCode})
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    <tr>
                                        <th style={{width: "20%", padding: "10px"}}>엑셀 업로드</th>
                                        <td style={{width: "80%", padding: "10px"}}>
                                            <div style={{display: "flex", alignItems: "center", marginBottom: "8px"}}>
                                                <input
                                                    type="file"
                                                    id="uploadFile"
                                                    name="file"
                                                    accept=".xlsx,.xls"
                                                    className="input-customtext"
                                                    style={{marginRight: "10px"}}
                                                    {...register("file")}
                                                />
                                            </div>
                                            <div style={{marginBottom: "4px"}}>
                                                <small>먼저 샘플양식을 다운로드하세요. 복수 시트는 지원하지 않습니다.</small>
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <div className="btn-right-box" style={{marginBottom: "20px"}}>
                                    <ul>
                                        <li>
                                            <button type="button"
                                                    className="btn-excel"
                                                    onClick={handleSampleDownload}
                                                    disabled={formLoading}
                                            >
                                                {formLoading ? "다운로드 중..." : "샘플양식 다운로드"}
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="submit"
                                                className="btn-write"
                                                id="preview"
                                                disabled={formLoading || !selectedCompCode || !selectedSiteCode}
                                            >
                                                {formLoading ? "처리 중..." : "미리보기"}
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className="btn-write"
                                                id="save"
                                                onClick={saveData}
                                                disabled={formLoading || previewData.length === 0}>
                                                {formLoading ? "등록 중..." : "등록"}
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                {/* 오류 메시지 표시 */}
                                {errorMsg && (
                                    <div style={{
                                        marginBottom: "20px",
                                        padding: "15px",
                                        backgroundColor: "#f8d7da",
                                        border: "1px solid #f5c6cb",
                                        borderRadius: "4px",
                                        color: "#721c24"
                                    }}>
                                        <div style={{marginBottom: "10px"}}>
                                            <strong>❌ 등록 실패:</strong>
                                        </div>
                                        <div style={{
                                            whiteSpace: "pre-line",
                                            lineHeight: "1.5",
                                            fontSize: "14px"
                                        }}>
                                            {errorMsg}
                                        </div>
                                        <div style={{
                                            marginTop: "10px",
                                            fontSize: "13px",
                                            color: "#721c24"
                                        }}>
                                            💡 <strong>해결 방법:</strong>
                                            <ul style={{margin: "5px 0 0 20px", padding: 0}}>
                                                <li>엑셀 파일에서 해당 행의 데이터를 확인하세요</li>
                                                <li>부서명과 직위명이 해당 회사에 등록된 것과 정확히 일치하는지 확인하세요</li>
                                                <li>안전직무는 기준관리 → 공통코드 → SAFE_ROLE의 공통코드명을 정확히 입력하세요</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="section-tit-wrap">
                                <h2 className="border-left-primary" style={{marginTop: "10px", marginBottom: "20px"}}>
                                    &nbsp;업로드 데이터 미리보기
                                    <span className="total-txt">
                                        {" "} | 총 <span className="red">{previewData.length || 0}</span> 건
                                    </span>
                                </h2>
                            </div>

                            <div className="table-container-fullscreen">
                                <table className="table-list" style={{ width: "100%" }}>
                                    <colgroup>
                                        <col style={{ width: "15%" }} />
                                        <col style={{ width: "15%" }} />
                                        <col style={{ width: "15%" }} />
                                        <col style={{ width: "15%" }} />
                                        <col style={{ width: "15%" }} />
                                        <col style={{ width: "25%" }} />
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th>이름</th>
                                        <th>부서</th>
                                        <th>직위</th>
                                        <th>안전직무</th>
                                        <th>연락처</th>
                                        <th>비고</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {previewData.length > 0 ? (
                                        previewData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.empName}</td>
                                                <td>{row.deptName}</td>
                                                <td>{row.posName}</td>
                                                <td>{row.roleName}</td>
                                                <td>{row.phone}</td>
                                                <td>{row.notice || "-"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: "center" }}>
                                                업로드한 데이터가 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {/* 데이터 개수 표시 */}
                            {previewData.length > 0 && (
                                <div style={{marginTop: "10px", textAlign: "right", fontSize: "14px", color: "#666"}}>
                                    총 {previewData.length}건의 사원 데이터
                                </div>
                            )}

                            {/* 주의사항 안내 */}
                            {!selectedCompCode || !selectedSiteCode ? (
                                <div style={{
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: "#fff3cd",
                                    border: "1px solid #ffeaa7",
                                    borderRadius: "4px",
                                    color: "#856404"
                                }}>
                                    <strong>⚠️ 주의:</strong> 회사와 사업장을 모두 선택해야 사원 등록이 가능합니다.
                                </div>
                            ) : null}
                        </form>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export { EmployeeExcelUpload };